import React from "react";
import { NavLink } from "react-router-dom";
import { getAvailableYears } from "../../../../utils/getAvailableYears";
import moment from "moment";
import { Contract } from "../../../../store";
import styled from "styled-components";

/**
 * Props for MeteringPointYearSelector component.
 */
type Props = {
  /** List of available contracts containing metering point data. */
  contracts: Array<Contract>;
  /** Current pathname used for navigation. */
  pathname: string;
  failedYear: string;
};

/**
 * Component for selecting a metering point and a year to navigate to consumption data details page.
 * Allows users to pick a metering point and a year, then generates a navigation link.
 *
 * @component
 */
const MeteringPointYearSelector: React.FC<Props> = ({ contracts, pathname, failedYear }) => {
    const [selectedMeteringPoint, setSelectedMeteringPoint] = React.useState(
      contracts.length > 0 ? contracts[0].meteringpointIdentifier : ""
    );
    const [selectedYear, setSelectedYear] = React.useState(moment().format("YYYY"));
  
    // Get year list based on the selected metering point, and remove the year that failed to load
    const availableYears = getAvailableYears(contracts, selectedMeteringPoint)
    .filter((year) => year !== Number(failedYear))
    .sort((a, b) => b - a);

    React.useEffect(() => {
      if (
        availableYears.length > 0 &&
        !availableYears.includes(Number(selectedYear))
      ) {
        setSelectedYear(availableYears[0].toString());
      }
    }, [availableYears]);
      

    return (
      <SelectorWrapper>
        <Label>Valitse mittauspiste, sekä vuosi:</Label>
  
        {/* Metering Point Selector */}
        <StyledSelect
          onChange={(e) => setSelectedMeteringPoint(e.target.value)}
          value={selectedMeteringPoint}
          aria-label="Select metering point"
        >
          {contracts.map((contract) => (
            <option key={contract.meteringpointIdentifier} value={contract.meteringpointIdentifier}>
              {contract.address}, {contract.zip} {contract.city}
            </option>
          ))}
        </StyledSelect>
  
        {/* Year Selector */}
        <StyledSelect
          onChange={(e) => setSelectedYear(e.target.value)}
          value={selectedYear}
          aria-label="Select year"
        >
          {availableYears.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </StyledSelect>
  
        {/* Navigation Button */}
        {selectedMeteringPoint && selectedYear && (
          <StyledNavLink to={`/${pathname}/${selectedMeteringPoint}/${selectedYear}`}>
            Siirry vuoden {selectedYear} kulutsnäkymään
          </StyledNavLink>
        )}
      </SelectorWrapper>
    );
  };  

export default MeteringPointYearSelector;

/* Styled Components */
const SelectorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 8px;
`;

const Label = styled.p`
  color: #333;
  font-size: 16px;
  margin-bottom: 10px;
`;

const StyledSelect = styled.select`
  width: 100%;
  max-width: 300px;
  padding: 8px;
  font-size: 16px;
  border-radius: 4px;
  border: 1px solid #ccc;
  margin-bottom: 10px;
  background: #fff;
  cursor: pointer;
`;

const StyledNavLink = styled(NavLink)`
  display: inline-block;
  background: #007bff;
  color: #fff;
  text-decoration: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 16px;
  margin-top: 10px;

  &:hover {
    background: #0056b3;
  }
`;
